import 'photoswipe/style.css';

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import React from 'react';

import { Div } from './HTMLElement';
import { Icon, IconProps } from './Icon';
import { LightboxContext, LightboxItemsDimensions } from './LightboxContext';
import { createUUID } from './util';

export interface LightboxProps<IconName> {
  arrowKeys?: boolean;
  arrowNextIcon?: IconProps<IconName, number>;
  arrowPrevIcon?: IconProps<IconName, number>;
  backgroundClickAction?: ClickAction;
  backgroundOpacity?: number;
  children?: React.ReactNode;
  closeIcon?: IconProps<IconName, number>;
  closeOnVerticalDrag?: boolean;
  cursor?: React.CSSProperties['cursor'];
  doubleTapAction?: ClickAction;
  escKey?: boolean;
  imageClickAction?: ClickAction;
  initialZoomLevel?: 1 | 'fill' | 'fit';
  loop?: boolean;
  padding?: number;
  pinchToClose?: boolean;
  showHideAnimationType?: 'zoom' | 'fade' | 'none';
  tapAction?: ClickAction;
  wheelToZoom?: boolean;
  zoomIcon?: IconProps<IconName, number>;
}

type ClickAction = 'zoom' | 'zoom-or-close' | 'toggle-controls' | 'next' | 'close';
const focusableEl = ['a[href]', 'button:not([disabled])', '[tabindex]:not([tabindex="-1"])'].join(', ');
export const Lightbox = <IconName extends string>({
  arrowKeys = true,
  arrowNextIcon,
  arrowPrevIcon,
  backgroundClickAction = 'close',
  backgroundOpacity = 0.8,
  children,
  closeIcon,
  closeOnVerticalDrag = true,
  cursor = 'zoom-in',
  doubleTapAction = 'zoom',
  escKey = true,
  imageClickAction = 'zoom-or-close',
  initialZoomLevel = 'fit',
  loop = true,
  padding = 0,
  pinchToClose = true,
  showHideAnimationType = 'zoom',
  tapAction = 'toggle-controls',
  wheelToZoom,
  zoomIcon,
}: LightboxProps<IconName>) => {
  const id = React.useMemo(() => `lightbox-${createUUID()}`, []);

  const arrowNextIconRef = React.useRef<HTMLDivElement>(null);
  const arrowPrevIconRef = React.useRef<HTMLDivElement>(null);
  const zoomIconRef = React.useRef<HTMLDivElement>(null);
  const closeIconRef = React.useRef<HTMLDivElement>(null);

  const [allDimensions, setAllDimensions] = React.useState<LightboxItemsDimensions>({});

  React.useEffect(() => {
    let lightbox: PhotoSwipeLightbox | null = new PhotoSwipeLightbox({
      arrowKeys: arrowKeys,
      arrowNextSVG: arrowNextIconRef.current?.innerHTML,
      arrowPrevSVG: arrowPrevIconRef.current?.innerHTML,
      bgClickAction: backgroundClickAction,
      bgOpacity: backgroundOpacity,
      children: 'a',
      closeOnVerticalDrag: closeOnVerticalDrag,
      closeSVG: closeIconRef.current?.innerHTML,
      doubleTapAction: doubleTapAction,
      escKey: escKey,
      gallery: `#${id}`,
      imageClickAction: imageClickAction,
      initialZoomLevel: initialZoomLevel,
      loop: loop,
      padding: padding ? { bottom: padding, left: padding, right: padding, top: padding } : undefined,
      pinchToClose: pinchToClose,
      pswpModule: () => import('photoswipe'),
      showHideAnimationType: showHideAnimationType,
      tapAction: tapAction,
      wheelToZoom: wheelToZoom,
      zoomSVG: zoomIconRef.current?.innerHTML,
    });

    let previouslyFocused: HTMLElement | null = null;

    const getFocusableElements = (pswp: HTMLElement): Array<HTMLElement> =>
      Array.from(pswp.querySelectorAll<HTMLElement>(focusableEl)).filter((el) => !el.dataset.pswpSentinel);

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const pswp = document.querySelector<HTMLElement>('.pswp');
      if (!pswp) return;

      const focusable = getFocusableElements(pswp);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (!pswp.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey) {
        if (active === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    lightbox.on('beforeOpen', () => {
      previouslyFocused = document.activeElement as HTMLElement;
      document.addEventListener('keydown', trapFocus as EventListener);
    });

    lightbox.on('firstUpdate', () => {
      const pswp = document.querySelector<HTMLElement>('.pswp');
      if (!pswp) return;

      Array.from(document.body.children).forEach((el) => {
        if (el !== pswp) {
          (el as HTMLElement & { inert: boolean }).inert = true;
        }
      });

      const sentinelStart = document.createElement('div');
      sentinelStart.setAttribute('tabindex', '0');
      sentinelStart.setAttribute('aria-hidden', 'true');
      sentinelStart.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
      sentinelStart.dataset.pswpSentinel = 'start';

      const sentinelEnd = document.createElement('div');
      sentinelEnd.setAttribute('tabindex', '0');
      sentinelEnd.setAttribute('aria-hidden', 'true');
      sentinelEnd.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
      sentinelEnd.dataset.pswpSentinel = 'end';

      pswp.insertBefore(sentinelStart, pswp.firstChild);
      pswp.appendChild(sentinelEnd);

      sentinelStart.addEventListener('focus', () => {
        const focusable = getFocusableElements(pswp);
        focusable[focusable.length - 1]?.focus();
      });

      sentinelEnd.addEventListener('focus', () => {
        const focusable = getFocusableElements(pswp);
        focusable[0]?.focus();
      });

      requestAnimationFrame(() => {
        const focusable = getFocusableElements(pswp);
        focusable[0]?.focus();
      });
    });

    lightbox.on('destroy', () => {
      document.removeEventListener('keydown', trapFocus as EventListener);

      Array.from(document.body.children).forEach((el) => {
        (el as HTMLElement & { inert: boolean }).inert = false;
      });

      document.querySelectorAll('[data-pswp-sentinel]').forEach((el) => el.remove());

      previouslyFocused?.focus();
      previouslyFocused = null;
    });

    lightbox.on('loadComplete', (event) => {
      const src = event.content.data.src;
      const element = event.content.element as HTMLImageElement;
      if (src !== undefined) {
        setAllDimensions((prevState) => ({
          ...prevState,
          [src]: { height: element.naturalHeight, width: element.naturalWidth },
        }));
      }
    });

    lightbox.init();

    return () => {
      document.removeEventListener('keydown', trapFocus as EventListener);
      Array.from(document.body.children).forEach((el) => {
        (el as HTMLElement & { inert: boolean }).inert = false;
      });
      document.querySelectorAll('[data-pswp-sentinel]').forEach((el) => el.remove());
      lightbox !== null && lightbox.destroy();
      lightbox = null;
    };
  }, [
    arrowKeys,
    backgroundClickAction,
    backgroundOpacity,
    closeOnVerticalDrag,
    doubleTapAction,
    escKey,
    id,
    imageClickAction,
    initialZoomLevel,
    loop,
    padding,
    pinchToClose,
    showHideAnimationType,
    tapAction,
    wheelToZoom,
  ]);

  return (
    <>
      <div className="pswp-gallery" id={id}>
        <LightboxContext allDimensions={allDimensions} cursor={cursor} id={id} setAllDimensions={setAllDimensions}>
          {children}
        </LightboxContext>
      </div>
      {arrowNextIcon && (
        <Div display="none">
          <div ref={arrowNextIconRef}>
            <Icon {...arrowNextIcon} />
          </div>
        </Div>
      )}
      {arrowPrevIcon && (
        <Div display="none">
          <div ref={arrowPrevIconRef}>
            <Icon {...arrowPrevIcon} />
          </div>
        </Div>
      )}
      {closeIcon && (
        <Div display="none">
          <div ref={closeIconRef}>
            <Icon {...closeIcon} />
          </div>
        </Div>
      )}
      {zoomIcon && (
        <Div display="none">
          <div ref={zoomIconRef}>
            <Icon {...zoomIcon} />
          </div>
        </Div>
      )}
    </>
  );
};
